/**
 * 海报舱**统一来源条**(V1 §3.4,2026-07-25)—— 「我从哪来、它替我定了什么、怎么换」。
 *
 * ═══ 收的是哪三处散写 ═══════════════════════════════════════════════════════════
 * 改之前同一件事在舱内有三套壳,而且各说各的:
 *   1. `SceneOriginBar`  —— 创建页 + 搭建器,只说「来自某张卡」;
 *   2. `PosterBlueprintBar` —— 组图蓝图,富信息(爆点可增删改);
 *   3. 搭建器里另写的 handoff 横幅 —— 又是一套 markup,说的还是蓝图这件事。
 * 加上「智能规划稿」那个 chip 和「放弃智能稿」按钮散在页头,一共四处在回答同一个问题。
 * 用户看到的是四种长相;工程上是改一处漏三处。故收成这一个挂载点、一套形制:
 *     [图标/缩略图] [来源类别 tag] [这一份具体是什么] [它替你定了什么] [换/清除]
 * 形制走共享 `shared/ui/SourceStrip`(与视频舱波1 同一份;2026-07-25 从两边各一份拷贝抽出来的)。
 *
 * ═══ ★ 收敛的是挂载点与判据,不是把不同信息量压成同一个壳 ═══════════════════════
 * `explosive` 在**创建页**带的是可增删改的爆点列表 —— 那是这条来源最要紧的信息,
 * 压进两行 strip 就丢了编辑能力。所以它原样嵌蓝图工作卡(`BlueprintHeroCard`,见下方
 * BPWB-1 一节;与视频侧 `VideoSourceBar` 同一个判断、同一个组件)。
 * 而在**搭建器**里,爆点早已物化成区块 —— 用户改的是区块不是爆点,再给一个爆点编辑器
 * 就是两份真相。故同一个 kind 用 `editable: null` 表达「已物化」,只说明来路。
 *
 * ═══ ★ `scene` 不给假「退出」 ══════════════════════════════════════════════════
 * 搭建器已按用途把区块铺好,退不回「没有区块」的状态,给退出按钮等于假承诺
 * (`SceneOriginBar` 原有的正确判断,原样继承:不传 `onClear` 就不渲染)。
 * 创建页那条可以退 —— 它的场景效果(组图模式 / 套图画幅)是可复位的纯 state。
 *
 * ★`template` / `product` 两个 kind 本波先落契约与渲染,挂载点分别在波A(模板迁长图)
 *  与波B(产品树进商品出图)接上 —— 先定形制,免得下一波又长出第五种壳。
 *
 * ═══ ★ BPWB-1(2026-09-03)explosive 细条 → 蓝图工作卡 ═══════════════════════════
 * `PosterBlueprintBar` 退役:有真蓝图(`formulaName` 在)时两个 explosive 子态
 * (创建页可编辑 banner / 搭建器已物化态)统一换成 `BlueprintHeroCard`(与视频舱
 * `VideoSourceBar` 同一份形制、同一个组件)。无蓝图的手动组图态(用户直接切出图模式,
 * `formulaName` 为 null)不进蓝图卡 —— 「已套用蓝图」在没有蓝图时是假话,原地保留一条
 * 极简 banner。
 */
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { Flame, LayoutTemplate, PencilLine, Package, RefreshCcw, Sparkles, X } from "lucide-react";

import { SCENE_CARDS } from "@/entities/scene-card/catalog";
import { sceneOriginChips } from "../../model/sceneOriginChips";
import { sceneOriginIcon } from "../../model/sceneOriginIcon";
import { Button } from "@/shared/ui/button";
import { SourceStrip } from "@/shared/ui/SourceStrip";
import type { PosterSourceBarData } from "../../model/posterSource";
import { BlueprintHeroCard, HandoffPreview, ReferenceContentPanel } from "@/entities/explosive";

interface Props {
  data: PosterSourceBarData;
  /** 提交中:整条冻结(参数已随请求走了,这时候换来源没有意义)。 */
  disabled?: boolean;
  className?: string;
}

export function PosterSourceBar({ data, disabled, className }: Props) {
  // 来源条上的积分 chip 与卡面同一个出处(价目表),不在这里重算
  const { book: priceBook } = usePriceBook();
  switch (data.kind) {
    case "explosive": {
      if (data.editable) {
        // ★2026-07-28:爆点本体在主区 GroupPointsEditor(今 GroupScriptPanel),这条只说来路 + 换/退。
        // ★2026-09-03(BPWB-1):有真蓝图 → 蓝图工作卡(承诺句 + 原片播放位);
        //   无蓝图的手动组图态(`formulaName` null)→ 极简 banner,不进蓝图卡。
        return (
          <div className="space-y-2">
            {data.formulaName ? (
              <BlueprintHeroCard
                formulaName={data.formulaName}
                sourcePlatform={data.sourcePlatform}
                blueprintId={data.blueprintId}
                hasRawVideo={data.hasRawVideo}
                disabled={disabled}
                onSwap={data.editable.onSwap}
                onClear={data.editable.onClear}
                pledge={
                  <>
                    借它的<b>张数、顺序、每张干什么</b>。参考句是别人家的话,只做提示 ——{" "}
                    <b>每张的标题要写你自己的</b>,不写的那张不会出图。
                  </>
                }
              />
            ) : (
              <div className="flex items-center gap-2 rounded-lg border-2 border-primary/60 bg-primary/5 px-3.5 py-3 text-sm">
                <Flame className="h-4 w-4 shrink-0 text-primary" />
                <span className="min-w-0 font-medium">组图模式 · 按爆点分张</span>
                <div className="flex-1" />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1 text-muted-foreground"
                  disabled={disabled}
                  onClick={data.editable.onClear}
                >
                  <X className="h-3 w-3" />
                  退出组图
                </Button>
              </div>
            )}
            {/* 2026-08-13:组图这条以前只看得见一个套路名,结构层全在看不见的 payload 里 */}
            <HandoffPreview payload={data.payload} />
            {/* 2026-08-14:内容层 —— 让用户看见我们把参考片拆到了逐拍逐句 */}
            {data.referenceContent && (
              <ReferenceContentPanel
                content={data.referenceContent}
                original={data.referenceContent}
                disabled={disabled}
                onChange={() => {}}
              />
            )}
          </div>
        );
      }
      return (
        <div className="space-y-2">
          <BlueprintHeroCard
            formulaName={data.formulaName ?? ""}
            sourcePlatform={data.sourcePlatform}
            blueprintId={data.blueprintId}
            hasRawVideo={data.hasRawVideo}
            disabled={disabled}
            onSwap={data.actions?.onSwap}
            onClear={data.actions?.onClear}
            pledge={
              <>
                借它的<b>分屏节奏和卖点顺序</b>。区块名里的「参考:…」是别人家的话,永远不会印进图 ——{" "}
                <b>每块的正文写你自己的</b>。
              </>
            }
          >
            {!!data.injectedBlocks && (
              <p className="text-xs text-muted-foreground">
                已注入 {data.injectedBlocks} 块,可自由增删改排
              </p>
            )}
          </BlueprintHeroCard>
          {/* 长图搭建器:爆点已物化成区块,但结构层(公式/节拍/槽位)同样看不见 */}
          <HandoffPreview payload={data.payload} />
          {data.referenceContent && (
            <ReferenceContentPanel
              content={data.referenceContent}
              original={data.referenceContent}
              disabled={disabled}
              onChange={() => {}}
            />
          )}
        </div>
      );
    }

    case "scene": {
      const card = data.sceneKey ? SCENE_CARDS.find((c) => c.key === data.sceneKey) : null;
      // 未知 / 历史 sceneKey 一律不渲染,不把原始 key 露给用户(与 sceneLabelOf 同口径)
      if (!card) return null;
      return (
        <SourceStrip
          domainAttr="data-poster-source"
          accent
          compact
          className={className}
          /* 2026-07-28 用户报「进来页面上面那个丑」。三处改动:
             ① compact —— 形制图示只有 16px,原来塞进 44×44 图标格里四周全是空白,
                缩完只剩几个灰线框;现在内联在标题前,图示与标题同量级
             ② 去掉「场景卡」tag —— 那是内部分类术语,用户不需要知道自己在「场景卡」里
             ③ 规格拆成 chip(画幅 / 积分),不再和用途拼成一句长话 */
          title={card.title}
          // 复述卡面那句「一句话用途」—— 单源,不另写一套文案(不会漂移)
          meta={card.usage}
          chips={sceneOriginChips(card, priceBook)}
          icon={(() => {
            const Icon = sceneOriginIcon(card.shape);
            return <Icon className="h-4 w-4" strokeWidth={1.8} />;
          })()}
          action={
            data.onClear && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 gap-1 text-muted-foreground"
                disabled={disabled}
                onClick={data.onClear}
                aria-label={`退出「${card.title}」场景,回到默认创建`}
              >
                <X className="h-3 w-3" />
                退出场景
              </Button>
            )
          }
        />
      );
    }

    case "template":
      return (
        <SourceStrip
          domainAttr="data-poster-source"
          accent
          className={className}
          tag="长图模板"
          title={data.name}
          meta={[
            data.blockCount ? `已预填 ${data.blockCount} 个区块` : "已预填骨架",
            data.withTokens ? "配色" : null,
          ]
            .filter(Boolean)
            .join(" + ")}
          thumbUrl={data.coverUrl}
          icon={<LayoutTemplate className="h-5 w-5" strokeWidth={1.8} />}
          action={
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 gap-1"
              disabled={disabled}
              onClick={data.onChange}
            >
              <RefreshCcw className="h-3 w-3" />
              换模板
            </Button>
          }
        />
      );

    case "plan":
      return (
        <SourceStrip
          domainAttr="data-poster-source"
          accent
          className={className}
          tag="AI 智能规划"
          title={data.productName ? `智能稿 · ${data.productName}` : "智能规划稿"}
          meta={`AI 已规划 ${data.blockCount} 块,可逐块精编`}
          icon={<Sparkles className="h-5 w-5" strokeWidth={1.8} />}
          action={
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 gap-1 text-muted-foreground"
              disabled={disabled}
              onClick={data.onDiscard}
            >
              <X className="h-3 w-3" />
              放弃智能稿
            </Button>
          }
        />
      );

    case "product":
      return (
        <SourceStrip
          domainAttr="data-poster-source"
          accent
          className={className}
          tag="产品库"
          title={data.productName}
          // ★波B 红线的对客表达:产品参数一个字都不许编 —— 这行就是那条红线说给用户听的样子
          meta="参数逐字取自产品库,未改写"
          icon={<Package className="h-5 w-5" strokeWidth={1.8} />}
          action={
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 gap-1"
              disabled={disabled}
              onClick={data.onChange}
            >
              <RefreshCcw className="h-3 w-3" />
              换产品
            </Button>
          }
        />
      );

    case "free":
      return (
        <SourceStrip
          domainAttr="data-poster-source"
          className={className}
          tag="自由创作"
          title="自己描述,不套目的"
          meta="一句话说要什么就行;想让系统按套路铺好,可以先挑个目的"
          icon={<PencilLine className="h-5 w-5" strokeWidth={1.8} />}
          action={
            data.onPickPurpose && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 gap-1 text-primary"
                disabled={disabled}
                onClick={data.onPickPurpose}
              >
                <Sparkles className="h-3 w-3" />
                挑个目的
              </Button>
            )
          }
        />
      );
  }
}
